import React, { useContext } from 'react';
import { useDeleteEvaluationMutation } from '../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { DELETE_EVALUATION } from '../../Reducers/Actions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import MuiDialog from '../../SystemDesign/MuiDialog';

/**
 * DeleteEvaluation Component - Allows deleting an evaluation
 * Mirrors the behavior of `components/Postes/DeletePoste.jsx`
 *
 * @param {Object} props
 * @param {Object} props.row - Row object containing the evaluation data (row.original)
 */
const DeleteEvaluation = ({ row }) => {
  const [deleteEvaluation, { isLoading }] = useDeleteEvaluationMutation();
  const dispatch = useDispatch();

  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  const evaluation = row?.original || {};

  // Support multiple id shapes
  const evId = evaluation?.evaluation_id ?? evaluation?.id ?? evaluation?.evaluationId;

  if (!evId) return null;

  const title = evaluation?.title || evaluation?.name || 'Evaluation';

  const handleDelete = async () => {
    try {
      try {
        // Call API
        const response = await deleteEvaluation({ evaluationId: evId }).unwrap();

        // Update Redux store IMMEDIATELY
        dispatch({ type: DELETE_EVALUATION, payload: { id: evId, evaluationId: evId, evaluation_id: evId } });

        // Extract success message
        let successMessage;
        if (typeof response === 'string') {
          successMessage = response;
        } else if (response && typeof response === 'object' && response.message) {
          successMessage = response.message;
        } else {
          successMessage = `Evaluation "${title}" deleted successfully`;
        }

        setInfoAlert({ severity: 'success', message: successMessage });
        setOpenSnackBar(true);
        return true;
      } catch (parseError) {
        // If unwrap parsing fails but HTTP status is 200, treat as success (plain text response)
        if (parseError?.originalStatus === 200) {
          dispatch({ type: DELETE_EVALUATION, payload: { id: evId, evaluationId: evId, evaluation_id: evId } });
          const successMessage = parseError.data || `Evaluation "${title}" deleted successfully`;
          setInfoAlert({ severity: 'success', message: successMessage });
          setOpenSnackBar(true);
          return true;
        }
        // eslint-disable-next-line no-console
        console.error('[DeleteEvaluation] API delete error path (non-200)', parseError);
        throw parseError;
      }
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      let errorMessage = 'An error occurred while deleting the evaluation';
      if (error?.data?.message) {
        errorMessage = typeof error.data.message === 'string' ? error.data.message : JSON.stringify(error.data.message);
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setInfoAlert({ severity: 'error', message: errorMessage });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <DeleteIcon fontSize="small" color="error" />
      </ListItemIcon>
      <ListItemText>Delete Evaluation</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <div>
      <p>Are you sure you want to delete {title}?</p>
      <p>This action is irreversible.</p>
    </div>
  );

  return (
    <MuiDialog
      icon={<DeleteOutlineIcon />}
      triggerButton={triggerButton}
      title="Delete"
      subtitle={`You are about to delete ${title}`}
      content={dialogContent}
      maxWidth="xs"
      existAction={true}
      actionTitle="Delete"
      actionFunction={handleDelete}
      isLoading={isLoading}
    />
  );
};

// Static props for menu integration
DeleteEvaluation.menuIcon = <DeleteOutlineIcon fontSize="small" />;
DeleteEvaluation.menuLabel = 'Delete';

export default DeleteEvaluation;

